import { useFormik } from "formik";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Spinner } from "reactstrap";
import * as Yup from "yup";
import { RegisterAPI } from "../../api/authAPIs";
import {
  Block,
  BlockContent,
  BlockHead,
  BlockTitle,
  Button,
  PreviewCard
} from "../../components/Component";
import LogoDark from "../../images/logo-dark.png";
import Logo from "../../images/logo.png";
import Head from "../../layout/head/Head";
import AuthFooter from "./AuthFooter";

const Register = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      username: "",
      email: "",
      password: "",
      phone: "",
      shop_name: "",
      shop_address: {
        street: "",
        city: "",
        state: "",
        postalCode: "",
        country: "",
      },
    },
    validationSchema: Yup.object({
      username: Yup.string().required("Username is required"),
      email: Yup.string().email("Invalid email address").required("Email is required"),
      password: Yup.string().min(4, "Password must be at least 4 characters").required("Password is required"),
      phone: Yup.string().required("Phone number is required"),
      shop_name: Yup.string().required("Shop name is required"),
      shop_address: Yup.object({
        street: Yup.string().required("Street is required"),
        city: Yup.string().required("City is required"),
        state: Yup.string().required("State is required"),
        postalCode: Yup.string().required("Postal code is required"),
        country: Yup.string().required("Country is required"),
      }),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const response = await RegisterAPI({ body: values });
        const data = response.data;
        toast.success(data.message);
        localStorage.setItem("accessToken", data.data.token);
        navigate(`${process.env.PUBLIC_URL}/auth-success`);
      } catch (error) {
        toast.error("An error occurred during registration");
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <>
      <Head title="Register" />
      <Block className="nk-block-middle nk-auth-body  wide-xs">
        <div className="brand-logo pb-4 text-center">
          <Link to={`${process.env.PUBLIC_URL}/`} className="logo-link">
            <img className="logo-light logo-img logo-img-lg" src={Logo} alt="logo" />
            <img className="logo-dark logo-img logo-img-lg" src={LogoDark} alt="logo-dark" />
          </Link>
        </div>
        <PreviewCard className="card-bordered" bodyClass="card-inner-lg">
          <BlockHead>
            <BlockContent>
              <BlockTitle tag="h4">Register</BlockTitle>
            </BlockContent>
          </BlockHead>
          <form className="is-alter" onSubmit={formik.handleSubmit}>
            <div className="form-group">
              <label className="form-label" htmlFor="username">
                Username
              </label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formik.values.username}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter your username"
                  className="form-control-lg form-control"
                />
                {formik.touched.username && formik.errors.username && (
                  <p className="invalid">{formik.errors.username}</p>
                )}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="email">
                Email
              </label>
              <div className="form-control-wrap">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter your email"
                  className="form-control-lg form-control"
                />
                {formik.touched.email && formik.errors.email && (
                  <p className="invalid">{formik.errors.email}</p>
                )}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="password">
                Password
              </label>
              <div className="form-control-wrap">
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter your password"
                  className="form-control-lg form-control"
                />
                {formik.touched.password && formik.errors.password && (
                  <p className="invalid">{formik.errors.password}</p>
                )}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="phone">
                Phone
              </label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter your phone number"
                  className="form-control-lg form-control"
                />
                {formik.touched.phone && formik.errors.phone && (
                  <p className="invalid">{formik.errors.phone}</p>
                )}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="shop_name">
                Shop Name
              </label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  id="shop_name"
                  name="shop_name"
                  value={formik.values.shop_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter your shop name"
                  className="form-control-lg form-control"
                />
                {formik.touched.shop_name && formik.errors.shop_name && (
                  <p className="invalid">{formik.errors.shop_name}</p>
                )}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="street">
                Street
              </label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  id="street"
                  name="shop_address.street"
                  value={formik.values.shop_address.street}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter street address"
                  className="form-control-lg form-control"
                />
                {formik.touched.shop_address?.street && formik.errors.shop_address?.street && (
                  <p className="invalid">{formik.errors.shop_address.street}</p>
                )}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="city">
                City
              </label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  id="city"
                  name="shop_address.city"
                  value={formik.values.shop_address.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter city"
                  className="form-control-lg form-control"
                />
                {formik.touched.shop_address?.city && formik.errors.shop_address?.city && (
                  <p className="invalid">{formik.errors.shop_address.city}</p>
                )}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="state">
                State
              </label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  id="state"
                  name="shop_address.state"
                  value={formik.values.shop_address.state}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter state"
                  className="form-control-lg form-control"
                />
                {formik.touched.shop_address?.state && formik.errors.shop_address?.state && (
                  <p className="invalid">{formik.errors.shop_address.state}</p>
                )}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="postalCode">
                Postal Code
              </label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  id="postalCode"
                  name="shop_address.postalCode"
                  value={formik.values.shop_address.postalCode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter postal code"
                  className="form-control-lg form-control"
                />
                {formik.touched.shop_address?.postalCode && formik.errors.shop_address?.postalCode && (
                  <p className="invalid">{formik.errors.shop_address.postalCode}</p>
                )}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="country">
                Country
              </label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  id="country"
                  name="shop_address.country"
                  value={formik.values.shop_address.country}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter country"
                  className="form-control-lg form-control"
                />
                {formik.touched.shop_address?.country && formik.errors.shop_address?.country && (
                  <p className="invalid">{formik.errors.shop_address.country}</p>
                )}
              </div>
            </div>

            <div className="form-group">
              <Button type="submit" color="primary" size="lg" className="btn-block">
                {loading ? <Spinner size="sm" color="light" /> : "Register"}
              </Button>
            </div>
          </form>
          <div className="form-note-s2 text-center pt-4">
            Already have an account? {" "}
            <Link to={`${process.env.PUBLIC_URL}/auth-login`}>
              <strong>Sign in instead</strong>
            </Link>
          </div>
        </PreviewCard>
      </Block>
      <AuthFooter />
    </>
  );
};

export default Register;
